import DateButtonGroup from '@/pages/Calendar/DateButtonGroup'
import { IUser } from '@/pages/Users/users.types'
import Button from '@/shared/Buttons/Button'
import SeparateLine from '@/shared/Forms/SeparateLine'
import { useRef, useState } from 'react'
import useMaintenancePlans from '@/pages/Calendar/hooks/useMaintenancePlans'
import useReportLayout from './hooks/useReportLayout'
import useReportCtrl from './hooks/useReportCtrl'
import ReportTableContent from '../ReportTableContent'
import ReportTableFooter from '../ReportTableFooter'
import ReportTableHeader from '../ReportTableHeader'
import useReportTableDownloadCtrl from '@/pages/MyOrganization/pages/MyOrganizationUsers/components/OrganizationUsersReportView/hooks/useReportTableDownloadCtrl'
import FileChartLineIcon from '@/assets/icons/fileChartLine.svg?react'
import { useNavigate } from 'react-router-dom'
import urls from '@/navigation/urls'
import UsersSortTypeDropdown from '../UsersSortTypeDropdown'
import ReportWidget from '../ReportWidget'
import useReportWidgetsCtrl from '../../hooks/useReportWidgetsCtrl'
import css from './organizationUsersReportView.module.css'
import userUtils from '@/pages/User/user.utils'
import GoToLinkButton from '@/shared/Buttons/GoToLinkButton'
import ReportIntervalModal from './ReportIntervalModal'

interface IProps {
  users: IUser[]
  usersLoading: boolean
}

export type TReportMaintenanceEvent =
  | 'PLANNED_MAINTENANCE'
  | 'OVERDUE_MAINTENANCE'
  | 'COMPLETED_MAINTENANCE'
  | 'UNPLANNED'

const OrganizationUsersReportView = (props: IProps) => {
  const tableContentRowsRef = useRef<HTMLDivElement>(null)
  const layoutCtrl = useReportLayout(tableContentRowsRef, props.users.length, 1366)
  const ctrl = useReportCtrl(layoutCtrl.viewingMode, props.users)
  const maintenancePlans = useMaintenancePlans(ctrl.data.filters, props.users)
  const navigate = useNavigate()
  const reportDownloadCtrl = useReportTableDownloadCtrl(ctrl.data.filters)
  const widgetsCtrl = useReportWidgetsCtrl(maintenancePlans.events, ctrl.data.sortedUsers)
  const [reportModalOpen, setReportModalOpen] = useState(false)

  const dataLoading = !maintenancePlans.isLoaded || props.usersLoading

  const renderBestUserWidgetContent = () => {
    if (!Boolean(widgetsCtrl.bestEmploye)) return '-'
    return (
      <GoToLinkButton
        renderGoToIcon={false}
        to={urls.employePage(widgetsCtrl.bestEmploye.id)}
        text={userUtils.getShortName(widgetsCtrl.bestEmploye.userInfo)}
      />
    )
  }

  return (
    <div className="flex-lines gap16 height100">
      <SeparateLine />
      <div className="flex-space-between">
        <div className="inline-flex-gap gap16 center">
          <DateButtonGroup
            viewingMode="range"
            onDateChange={(dates) => {
              maintenancePlans.resetLoadState()
              ctrl.handlers.handleDateChange(dates)
            }}
            selectedDates={ctrl.data.filters}
            selectedSection="week"
          />
          <Button
            size="default"
            type="default"
            onClick={() => {
              maintenancePlans.resetLoadState()
              ctrl.handlers.handleTodayClick()
            }}
            text="Сегодня"
          />
        </div>
        <Button
          loading={reportDownloadCtrl.loading}
          size="default"
          type="default"
          onClick={() => setReportModalOpen(true)}
          icon={<FileChartLineIcon width={16} height={16} />}
          text="Сформировать отчет"
        />
      </div>
      <div className={css.widgetsWrapper}>
        <ReportWidget title="Лучший сотрудник" value={renderBestUserWidgetContent()} loading={dataLoading} />
        <ReportWidget title="ТО выполнено" value={widgetsCtrl.completedCountByType} loading={dataLoading} />
        <ReportWidget title="Просрочено" value={widgetsCtrl.overdueCountByType} loading={dataLoading} />
        <ReportWidget title="Внеплановых ТО" value={maintenancePlans.events.unplanned.length} loading={dataLoading} />
      </div>
      <div className="flex-lines" style={{ height: 'calc(100% - 3 * 16px - 32px - 54px - 1px)' }}>
        <ReportTableHeader
          days={ctrl.data.daysRange}
          scrollWidth={layoutCtrl.scrollbarWidth}
          loading={dataLoading}
          hoverCol={ctrl.data.hoverCol}
          onHoverColChange={ctrl.handlers.handleHoverColChange}
          leadingCell={
            <UsersSortTypeDropdown
              value={ctrl.data.usersSortType}
              name="usersSortTypeDropdown"
              onChange={ctrl.handlers.handleUsersSortTypeChange}
              wrapperStyle={{ width: '288px' }}
            />
          }
        />
        <ReportTableContent
          days={ctrl.data.daysRange}
          ref={tableContentRowsRef}
          events={maintenancePlans.events}
          loading={dataLoading}
          users={ctrl.data.sortedUsers}
          hoverCol={ctrl.data.hoverCol}
          onHoverColChange={ctrl.handlers.handleHoverColChange}
          onUserCellClick={(user) => navigate(urls.employePage(user.id))}
        />
        <ReportTableFooter
          days={ctrl.data.daysRange}
          scrollWidth={layoutCtrl.scrollbarWidth}
          events={maintenancePlans.events}
          loading={dataLoading}
          hoverCol={ctrl.data.hoverCol}
        />
      </div>

      <ReportIntervalModal
        open={reportModalOpen}
        loading={reportDownloadCtrl.loading}
        onClose={() => setReportModalOpen(false)}
        onSubmit={async (interval) => {
          const isDownloaded = await reportDownloadCtrl.downloadMaintenanceReportXlsx(interval)
          if (isDownloaded) setReportModalOpen(false)
        }}
      />
    </div>
  )
}

export default OrganizationUsersReportView
